import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ButtonModel, ModalContext, Text } from '@notino/react-styleguide';
import { CatalogVariantFragmentFragment } from '@notino/shared/definitions/types';

import { useDrawer } from '@components/BottomSheet/DrawerProvider';
import { useFormatPrice } from '@components/PriceLabel/useFormattedPrice';
import { isDesktop } from '@utils/helpers';

import { getDiscountPercentage } from '../../../hooks/usePriceLogic/utils';

import { messages } from './messages';
import {
  LineThrough,
  ListItems,
  ModalContent,
  MoreInfoLink,
  OkButton,
} from './styled';
import { displayPercentageDiscount } from './utils';

const CrossedPrice = ({
  price,
  percentage,
}: {
  price: { value: number; currency: string };
  percentage: number | undefined;
}) => {
  const formatPrice = useFormatPrice();

  return (
    <>
      <Text
        style={{ marginTop: 4 }}
        typography="labelRegular400"
        color="text.secondary"
      >
        <LineThrough>
          {formatPrice(price)?.formattedPriceWithCurrency}
        </LineThrough>
      </Text>
      <Text
        typography="labelRegular400"
        color="text.highlight"
        style={{ marginLeft: 6 }}
      >
        {percentage !== undefined && displayPercentageDiscount(percentage)}
      </Text>
    </>
  );
};

type Props = {
  percentage: number;
  percentageFromMinimal: number | undefined;
  discountedPrice: number;
  legalText: React.ReactNode;
  variant: CatalogVariantFragmentFragment;
};

export const InfoModalContent = ({
  percentage,
  percentageFromMinimal,
  discountedPrice,
  legalText,
  variant,
}: Props) => {
  const minPrice = variant.recentMinPrice;
  const anchorPrice = variant.anchorPrice;
  const formatPrice = useFormatPrice();
  const { formatMessage, formatDate } = useIntl();
  const { hideModal } = ModalContext.useModalContext();
  const { hideDrawer } = useDrawer();

  const rows: { key: string; content: React.ReactNode }[] = [
    {
      key: 'priceAfterDiscount',
      content: (
        <>
          <p>
            <FormattedMessage {...messages.modalPriceAfterDiscountText} />
          </p>
          {legalText && (
            <Text
              block={true}
              typography="labelRegular400"
              color="text.tertiary"
            >
              *{legalText}
            </Text>
          )}
          <Text
            style={{ marginTop: 8 }}
            typography="titleMedium"
            color="text.highlight"
          >
            {
              formatPrice({ value: discountedPrice })
                ?.formattedPriceWithCurrency
            }
          </Text>
        </>
      ),
    },
    {
      key: 'currentPrice',
      content: (
        <>
          <p>
            <FormattedMessage {...messages.modalCurrentPriceText} />
          </p>
          <CrossedPrice
            price={variant.price as { value: number; currency: string }}
            percentage={percentage}
          />
        </>
      ),
    },
  ];

  if (minPrice) {
    rows.push({
      key: 'recentMinPrice',
      content: (
        <>
          <p>
            <FormattedMessage {...messages.modalRecentMinPriceText} />
          </p>
          <CrossedPrice
            price={minPrice as { value: number; currency: string }}
            percentage={percentageFromMinimal}
          />
        </>
      ),
    });
  }

  if (anchorPrice?.validFrom) {
    rows.push({
      key: 'anchorPrice',
      content: (
        <>
          <p>
            <FormattedMessage
              {...messages.modalAnchorPriceText}
              values={{
                date: formatDate(anchorPrice.validFrom, {
                  day: 'numeric',
                  month: 'numeric',
                  year: 'numeric',
                }),
              }}
            />
          </p>
          <CrossedPrice
            price={anchorPrice as { value: number; currency: string }}
            percentage={getDiscountPercentage(
              discountedPrice,
              anchorPrice.value ?? 0
            )}
          />
        </>
      ),
    });
  }

  return (
    <>
      {isDesktop() && (
        <Text typography="titleLarge">
          <FormattedMessage {...messages.modalTitle} />
        </Text>
      )}

      <ModalContent>
        <ListItems>
          {rows.map(({ key, content }, index) => (
            <Text key={key} typography="bodyRegular" as="p">
              <span>{index + 1}.</span>
              <div>{content}</div>
            </Text>
          ))}
        </ListItems>

        <MoreInfoLink
          target="_blank"
          href={formatMessage(messages.modalMoreInfoLinkUrl)}
        >
          <FormattedMessage {...messages.modalMoreInfoLinkText} />
        </MoreInfoLink>

        <OkButton
          onClick={() => (isDesktop() ? hideModal() : hideDrawer())}
          buttonStyle={ButtonModel.Styles.primary}
        >
          <FormattedMessage {...messages.modalOkButtonText} />
        </OkButton>
      </ModalContent>
    </>
  );
};
